<script setup lang="ts">
// TODO:
// - loader empty state
import { ID_USA } from '~/utils/constants/idsCountries'
import { getCountriesGlobalLottery, getLotteriesByCountry } from '~/utils/countries'
import CardLotteryResult from '~/components/pages/home/CardLotteryResult.vue'
import { useNotificationStore } from '~/store/notification'
import { countries as allCountries, IDS_LOTTERIES_CLASSIC, KENO_FR_TOTAL_NUMBERS } from '~/utils/constants/home.js'
import { useResultStore } from '~/store/result'
import { fetchLotterySelect } from '~/utils/playLottery.js'
import { ID_KENO_FR } from '~/utils/constants/idsLotteries'
import { filterOnlyNumbersByGroup, getOnlyNumbers } from '~/utils/numbers'
import { pickRandomNumbers } from '~/utils/getRandomNumbers'
import { trackCardCarousel } from '~/tracking/homeEvents'
import { geoCountryCodeToCountryId } from '~/composables/useGeoCountryCode'

defineOptions({
	name: 'GlobalLotteryResults',
})

interface QueryParams {
	lottery_id: number | undefined
	regular_balls?: any
	extra_balls?: any
	country_id: string | number | undefined
	page_number: number
	first_number?: any
	second_number?: any
	third_number?: any
	fourth_number?: any
	fifth_number?: any
	is_multiple_bets?: boolean
	number_of_balls?: number
	amount?: number
}

const geoCountryCode = useGeoCountryCode()
const detectedGeoCountryId = computed(() => geoCountryCodeToCountryId(geoCountryCode.value))

const notificationStore = useNotificationStore()
const resultStore = useResultStore()
const localePath = useLocalePath()
const router = useRouter()
const { t: $t } = useI18n()

const initSelectCountry = ref<number>(ID_USA)
const selected = ref<number>(ID_USA)
const userTouchedCountry = ref(false)
const didInitFromGeo = ref(false)

const countries = computed(() => {
	const safeLotteries = Array.isArray(notificationStore.getLasLotteries)
		? notificationStore.getLasLotteries.filter(Boolean)
		: []

	const IDS_COUNTRIES_WITH_LOTTERIES = safeLotteries
		.map((lottery) => lottery?.country_id)
		.filter(Boolean)
		.filter((value, index, self) => self.indexOf(value) === index)

	return getCountriesGlobalLottery(IDS_COUNTRIES_WITH_LOTTERIES)
})

const preferredCountryId = computed<number | null>(() => {
	return detectedGeoCountryId.value
})

const orderedCountries = computed(() => {
	const list = countries.value || []
	if (!list.length) return []

	const selectedId = initSelectCountry.value
	const geoId = preferredCountryId.value

	const pickId =
		(selectedId && list.some((c) => c.id === selectedId) ? selectedId : null) ??
		(geoId && list.some((c) => c.id === geoId) ? geoId : null) ??
		list[0].id

	const first = list.find((c) => c.id === pickId)
	if (!first) return list

	return [first, ...list.filter((c) => c.id !== pickId)]
})

const lotteriesByCountry = computed(() => {
	if (selected.value === null) return []

	const safeLotteries = Array.isArray(notificationStore.getLasLotteries)
		? notificationStore.getLasLotteries.filter(Boolean)
		: []

	return getLotteriesByCountry(safeLotteries, selected.value)
})

const getCountry = computed(() => {
	return allCountries.find((country) => country.id === selected.value) || countries.value?.[0] || null
})

const pillClass = (isActive: boolean) => {
	return [
		'rounded-full px-5 py-2 text-sm',
		'border border-white/10',
		'transition-colors',
		'shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]',
		isActive
			? 'bg-success-900 hover:bg-success-800 text-white'
			: 'bg-[#181c25] text-white/90 hover:bg-[#202735] hover:text-white',
	].join(' ')
}

const updateCountry = (value: any) => {
	const id = value?.id
	if (!id) return
	if (selected.value === id) return

	const firstCountryId = countries.value?.[0]?.id
	const existCountry = countries.value.some((item) => item.id === id)
	const countryId = existCountry ? id : firstCountryId

	if (!countryId) return
	selected.value = countryId
}

const setNumbersAndValidate = (numbers: any[], lottery_id: number) => {
	let regularBalls = numbers.filter((n: string | null) => n !== null)
	if (ID_KENO_FR === lottery_id) {
		regularBalls = pickRandomNumbers(numbers, KENO_FR_TOTAL_NUMBERS)
	}
	return regularBalls
}
const setNumbers = (lottery: any) => {
	const { regulars, extras, lottery_id } = lottery
	if (IDS_LOTTERIES_CLASSIC.includes(lottery_id)) {
		return {
			first: regulars,
			last: null,
		}
	}
	const regularBalls = setNumbersAndValidate(regulars, lottery_id)
	return {
		first: regularBalls,
		last: extras && extras.length > 0 ? extras : null,
	}
}

const setQueryParams = (lottery: any): QueryParams => {
	const { regulars: first, extras: last } = lottery
	const regularBalls = setNumbersAndValidate(first, lottery.lottery_id)

	const countryId = getCountry?.value?.id ?? countries.value?.[0]?.id

	let query: QueryParams = {
		lottery_id: lottery.lottery_id,
		regular_balls: regularBalls,
		country_id: countryId,
		page_number: 1,
	}

	if (last !== null) {
		query.extra_balls = last?.length > 0 ? last.filter((n: string | null) => n !== null) : null
	}

	if (ID_KENO_FR === lottery.lottery_id) {
		query.number_of_balls = regularBalls.length
		query.amount = 1
	}

	if (IDS_LOTTERIES_CLASSIC.includes(lottery.lottery_id)) {
		const [first_number, second_number, third_number, fourth_number, fifth_number] = first
		query = {
			lottery_id: lottery.lottery_id,
			country_id: lottery.country_id,
			first_number,
			second_number,
			third_number,
			fourth_number,
			fifth_number,
			page_number: 1,
		}
	}

	return query
}

const sendPageResults = (item: any) => {
	updateCountry({ id: item.country_id })

	resultStore.SET_STATE_RESULT({
		type: 'country',
		value: getCountry.value,
	})
	const lottery = fetchLotterySelect(item.lottery_id)
	resultStore.SET_STATE_RESULT({
		type: 'lottery',
		value: lottery,
	})
	const numbers = setNumbers(item)
	resultStore.SET_STATE_RESULT({
		type: 'numbers',
		value: filterOnlyNumbersByGroup(numbers),
	})
	resultStore.SET_STATE_RESULT({
		type: 'isMutipleBets',
		value: false,
	})
	resultStore.SET_STATE_RESULT({
		type: 'amount',
		value: 1,
	})

	const query = setQueryParams(item)
	router.push({ path: localePath('/results/lotteries'), query: query as unknown as Record<string, any> })
	trackCardCarousel({
		lottery_id: item.lottery_id,
		lottery_name: item.name,
		lottery_prize: item.prize,
		lottery_numbers: getOnlyNumbers(setNumbers(item)).join(','),
	})
}

const selectCountry = (countryId: string | number) => {
	userTouchedCountry.value = true
	selected.value = Number(countryId)
}

watch(
	() => detectedGeoCountryId.value,
	(countryId) => {
		if (!countryId) return
		if (userTouchedCountry.value) return
		if (didInitFromGeo.value) return

		const matched = allCountries.find((c) => Number(c.id) === countryId)
		if (!matched) return

		didInitFromGeo.value = true
		initSelectCountry.value = matched.id
		updateCountry(matched)
	},
	{ immediate: true },
)

watch(
	() => countries.value,
	(list) => {
		if (!list?.length) return

		const currentIsValid = list.some((c) => c.id === selected.value)
		if (currentIsValid) return

		// Si el selected no existe en la lista, usar el geo o el primero disponible
		const geoMatch = preferredCountryId.value ? list.find((c) => c.id === preferredCountryId.value) : null

		const fallback = geoMatch ?? list[0]
		initSelectCountry.value = fallback.id
		selected.value = fallback.id
	},
	{ immediate: true },
)
</script>

<template>
	<section
		id="global-lottery-results"
		class="sm:px-10 py-16 sm:py-20 bg-secondary-800"
	>
		<div class="container space-y-10 sm:space-y-16">
			<div class="space-y-6">
				<div class="text-primary-500 flex items-center gap-2 bg-primary-500/20 px-4 py-1 rounded-full w-fit">
					<UIcon
						name="i-feather:trending-up"
						class="size-4"
					/>
					<span class="text-sm">{{ $t('Real-Time Results') }}</span>
				</div>
				<div class="space-y-4">
					<h2 class="text-xl sm:text-4xl font-semibold text-white">{{ $t('Global Lottery Results') }}</h2>
					<p class="text-sm sm:text-base text-[rgba(255,255,255,0.45)] max-w-[580px]">
						{{
							$t(
								"Explore the latest winning numbers from the world's biggest lotteries. Hover over a card to see insights and check your numbers",
							)
						}}.
					</p>
				</div>
			</div>
			<div class="space-y-16 relative">
				<div class="flex items-center justify-between sm:justify-end">
					<UCarousel
						v-if="orderedCountries.length > 0"
						:items="orderedCountries"
						:ui="{ container: 'gap-2 px-4', item: 'snap-start' }"
						class="w-full sm:hidden"
					>
						<template #default="{ item }">
							<UButton
								:key="item.id"
								type="button"
								variant="ghost"
								class="shrink-0 touch-manipulation select-none"
								:class="pillClass(Number(selected) === Number(item.id))"
								@click.stop="selectCountry(item.id)"
							>
								<span class="text-base leading-none">{{ item.flag }}</span>
								<span class="whitespace-nowrap">{{ $t(item.label) }}</span>
							</UButton>
						</template>
					</UCarousel>

					<div class="w-[72px]" />
				</div>
				<div
					v-if="lotteriesByCountry.length > 0"
					class="flex sm:hidden flex-col gap-7"
				>
					<CardLotteryResult
						v-for="lottery in lotteriesByCountry"
						:key="lottery.lottery_id"
						:content-data="lottery"
						@click="sendPageResults(lottery)"
					/>
				</div>
				<UCarousel
					:items="notificationStore.getLasLotteries"
					class="w-full hidden sm:block"
					arrows
					:ui="{
						container: 'gap-6 overflow-y-visible py-2',
						item: 'snap-start shrink-0 basis-[380px] lg:basis-[410px] flex',
					}"
				>
					<template #default="{ item }">
						<CardLotteryResult
							class="h-full flex flex-col"
							:content-data="item"
							@click="sendPageResults(item)"
						/>
					</template>
					<template #prev="{ onClick, disabled }">
						<UButton
							icon="i-heroicons-arrow-left-20-solid"
							:variant="`${!disabled ? 'ghost' : 'link'}`"
							color="white"
							size="sm"
							class="absolute -top-24 right-12 z-10"
							:class="disabled ? 'text-white/40' : 'text-white hover:text-current'"
							:disabled="disabled"
							@click="onClick"
						/>
					</template>

					<template #next="{ onClick, disabled }">
						<UButton
							icon="i-heroicons-arrow-right-20-solid"
							:variant="`${!disabled ? 'ghost' : 'link'}`"
							color="white"
							size="sm"
							class="absolute -top-24 right-0 z-10"
							:class="disabled ? 'text-white/40' : 'text-white hover:text-current'"
							:disabled="disabled"
							@click="onClick"
						/>
					</template>
				</UCarousel>
			</div>
		</div>
	</section>
</template>

<style>
.scrollbar-hide {
	-ms-overflow-style: none;
	scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
	display: none;
}
</style>
